import React, { ReactNode } from 'react';
import { TbcFightGroupable } from '../types';
import { groupFightsByBoss } from '../utils';

export interface TbcBreakdownColumn<T> {
    key: string;
    header: ReactNode;
    render: (row: T) => ReactNode;
    /** Combined value for all pulls in a boss group (shown on the summary row). */
    aggregate?: (rows: T[]) => ReactNode;
    headerClassName?: string;
}

interface TbcFightBreakdownTableProps<T extends TbcFightGroupable> {
    fights: T[];
    columns: TbcBreakdownColumn<T>[];
    rowKey: (row: T) => string;
    emptyMessage?: string;
    renderFightLabel?: (row: T) => ReactNode;
}

function TbcFightBreakdownTable<T extends TbcFightGroupable>({
    fights,
    columns,
    rowKey,
    emptyMessage = 'No fights matched the selected filter.',
    renderFightLabel,
}: TbcFightBreakdownTableProps<T>) {
    const bossGroups = groupFightsByBoss(fights);
    const columnCount = columns.length;

    return (
        <div className="table-container sticky-head" style={{ marginTop: '20px' }}>
            <table className="breakdown-table">
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} className={column.headerClassName}>
                                {column.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {bossGroups.length === 0 && (
                        <tr>
                            <td colSpan={columnCount}>{emptyMessage}</td>
                        </tr>
                    )}
                    {bossGroups.map((group) => (
                        <React.Fragment key={group.key}>
                            <tr className="boss-group-header">
                                <td colSpan={columnCount}>
                                    {group.name}
                                    <span className="boss-group-count">
                                        {' '}
                                        — {group.fights.length} fight
                                        {group.fights.length === 1 ? '' : 's'}
                                    </span>
                                </td>
                            </tr>
                            {group.fights.map((fight) => (
                                <tr key={rowKey(fight)}>
                                    {columns.map((column, index) => {
                                        if (index === 0 && renderFightLabel) {
                                            return <td key={column.key}>{renderFightLabel(fight)}</td>;
                                        }
                                        return <td key={column.key}>{column.render(fight)}</td>;
                                    })}
                                </tr>
                            ))}
                            {columns.some((column) => column.aggregate) && (
                                <tr className="boss-group-summary">
                                    {columns.map((column, index) => {
                                        if (index === 0) {
                                            return (
                                                <td key={column.key}>
                                                    {column.aggregate ? column.aggregate(group.fights) : 'Boss avg'}
                                                </td>
                                            );
                                        }
                                        if (column.aggregate) {
                                            return <td key={column.key}>{column.aggregate(group.fights)}</td>;
                                        }
                                        return (
                                            <td key={column.key} className="boss-group-summary-empty">
                                                —
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default TbcFightBreakdownTable;
